import ReactECharts from 'echarts-for-react';
import { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import { useSelector } from "react-redux";
import '../../../../cssfiles/homepage.css';
import dayjs from 'dayjs';
import { config } from 'process';

// Loading spinner component
const LoadingSpinner = () => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '85%',
        width: '100%'
    }}>
        <div style={{
            border: '3px solid #f3f3f3',
            borderTop: '3px solid #3498db',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            animation: 'spin 1s linear infinite'
        }}></div>
        <style>
            {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}
        </style>
    </div>
);

// Error component
const ErrorDisplay = ({ message }) => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '85%',
        width: '100%',
        color: '#ff6b6b',
        fontSize: '14px',
        textAlign: 'center',
        padding: '20px'
    }}>
        <div>
            <div>...loading chart</div>
            {message && <div style={{ fontSize: '12px', marginTop: '8px' }}>{message}</div>}
        </div>
    </div>
);

const LineChartnewjson = (props) => {
    // Safe selector calls with fallbacks
    const tagaliascollectionvaluesnewjson = useSelector((state) => 
        state?.mainpagedata?.tagaliascollectionvaluesnewjson || {});
    const tagcollectionvaluesnewjson = useSelector((state) => 
        state?.mainpagedata?.tagcollectionvaluesnewjson || {});
    const dateformatselected = useSelector((state) => 
        state?.mainpagedata?.selecteddate || 'Custom');
    const sdate_edate_selected = useSelector((state) => 
        state?.mainpagedata?.selecteddates || null);
    const dateselected = useSelector((state) => 
        state?.mainpagedata?.selecteddate || null);
    const theme = useSelector((state) => 
        state?.themedata?.theme || 'light');

    const [seriesChart, setSeriesChart] = useState([]);
    const [options, setOptions] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasData, setHasData] = useState(false);
    const [debugInfo, setDebugInfo] = useState('');


    // Safe property access helper
    const safeGet = (obj, path, defaultValue = null) => {
        try {
            return path.split('.').reduce((current, key) => 
                current && current[key] !== undefined ? current[key] : defaultValue, obj);
        } catch {
            return defaultValue;
        }
    };

    // Validate props structure
    const validateProps = () => {
        if (!props) {
            throw new Error('Props are not available');
        }
        if (!props.widgetprop) {
            throw new Error('Widget properties are not available');
        }
        if (!props.widgetprop.props) {
            throw new Error('Widget props configuration is missing');
        }
        if (!props.widgetprop.props.extended) {
            throw new Error('Extended properties are missing');
        }
        if (!Array.isArray(props.widgetprop.props.extended.tagprops)) {
            throw new Error('Tag properties are not properly configured');
        }
    };

    useEffect(() => {
        const loadChart = async () => {
            try {
                setIsLoading(true);
                setError(null);
                setHasData(false);
                setDebugInfo('Initializing...');

                // Wait a tick to ensure state updates
                await new Promise(resolve => setTimeout(resolve, 10));

                // Validate props structure
                setDebugInfo('Validating props...');
                validateProps();

                
                const tagsbasedProperties = safeGet(props, 'widgetprop.props.extended.tagprops', []);
                
                if (!Array.isArray(tagsbasedProperties) || tagsbasedProperties.length === 0) {
                    setDebugInfo('No tag properties found in props');
                    throw new Error('No tag properties found');
                }

                setDebugInfo(`Found ${tagsbasedProperties.length} tag properties`);

                let configbasedProperties = [];

                for (let i = 0; i < tagsbasedProperties.length; i++) {
                    const currentconfigkeyitem = safeGet(tagsbasedProperties[i], 'configkeys', []);
                    if (Array.isArray(currentconfigkeyitem)) {
                        configbasedProperties = configbasedProperties.concat(currentconfigkeyitem);
                    }
                }

                const tagProperties = tagsbasedProperties.concat(configbasedProperties);
                setDebugInfo(`Processing ${tagProperties.length} total properties`);

                // Check if we have data in Redux store
                const hasTagData = Object.keys(tagcollectionvaluesnewjson).length > 0;
                const hasAliasData = Object.keys(tagaliascollectionvaluesnewjson).length > 0;
                
                setDebugInfo(`Redux data - Tags: ${hasTagData}, Aliases: ${hasAliasData}`);

                if (!hasTagData) {
                    setDebugInfo('No tag collection data in Redux store');
                    throw new Error('No data available in Redux store');
                }

                const colorPropertiesLines = [];
                const widthPropertiesLines = [];
                const curvePropertiesLines = [];
                const legendNamePropertiesLines = [];

                // Process tag properties safely
                tagProperties.forEach((chartType, index) => {
                    try {
                        const color = safeGet(chartType, 'chartprops.color', `#${Math.floor(Math.random()*16777215).toString(16)}`);
                        colorPropertiesLines.push(color);
                        widthPropertiesLines.push(2);
                        curvePropertiesLines.push('smooth');
                        
                        let legendName = '';
                        if (chartType && chartType.hasOwnProperty("tagid") && chartType.tagid) {
                            legendName = tagaliascollectionvaluesnewjson[chartType.tagid] || chartType.tagid;
                        } else if (chartType && chartType.hasOwnProperty("key") && chartType.key) {
                            legendName = tagaliascollectionvaluesnewjson[chartType.key] || chartType.key;
                        } else {
                            legendName = `Series ${index + 1}`;
                        }
                        legendNamePropertiesLines.push(legendName);
                    } catch (err) {
                        // Add defaults for failed processing
                        colorPropertiesLines.push('#000000');
                        widthPropertiesLines.push(2);
                        curvePropertiesLines.push('smooth');
                        legendNamePropertiesLines.push(`Series ${index + 1}`);
                    }
                });

                setDebugInfo('Processing series data...');

                // Process series data safely
                const seriesConsumed = tagProperties.map((chartType, index) => {
                    try {
                        
                        let dataKey = null;
                        let seriesName = `Series ${index + 1}`;

                        if (chartType && chartType.hasOwnProperty("tagid") && tagcollectionvaluesnewjson.hasOwnProperty(chartType.tagid)) {
                            dataKey = chartType.tagid;
                            seriesName = tagaliascollectionvaluesnewjson[chartType.tagid] || chartType.tagid;
                          
                        } else if (chartType && chartType.hasOwnProperty("key") && tagcollectionvaluesnewjson.hasOwnProperty(chartType.key)) {
                            dataKey = chartType.key;
                            seriesName = tagaliascollectionvaluesnewjson[chartType.key] || chartType.key;
                            
                        }

                        if (!dataKey) {
                            
                            return null;
                        }

                        const charttype = safeGet(chartType, 'charttype', '');
                        if (charttype === "maxlimit" || charttype === "minlimit") {
                            return null;
                        }

                        const rawData = tagcollectionvaluesnewjson[dataKey];
                        
                        if (!rawData || !Array.isArray(rawData)) {
                            return {
                                name: seriesName,
                                type: "line",
                                data: []
                            };
                        }

                                            
                        const processedData = rawData
                        .map(obj => {
                            const x = safeGet(obj, 'x', null);
                            const y = safeGet(obj, 'y', null);
                            
                            // Always keep x values if they exist, but allow null y values
                            if (x === null || !isFinite(Number(x))) {
                            return null; // Only filter out if x is invalid
                            }
                            
                            // Keep null y values instead of filtering them out
                            const yValue = (y === null || !isFinite(Number(y))) ? null : Number(y);
                            
                            return { x: Number(x), y: yValue };
                        })
                        .filter(item => item !== null); // Only filter out items with invalid x values

                        return {
                            name: seriesName,
                            type: "line",
                            data: processedData
                        };
                    } catch (err) {
                        return null;
                    }
                }).filter(item => item !== null);

                setDebugInfo(`Processed ${seriesConsumed.length} series`);

                // Check if we have any valid series with data
                const seriesWithData = seriesConsumed.filter(series => 
                    series && series.data && Array.isArray(series.data) && series.data.length > 0
                );


                if (seriesWithData.length === 0) {
                    setDebugInfo('No valid data series found - all series are empty');
                    throw new Error('No valid data series found');
                }

                // Rest of the options configuration...
                setDebugInfo('Configuring chart options...');

                // Safe property access for widget properties
                const fontSize = safeGet(props, 'widgetprop.props.extended.fontsize', '12px');
                const fontFamily = safeGet(props, 'widgetprop.props.extended.fontfamily', 'Arial');
                const fontWeight = safeGet(props, 'widgetprop.props.extended.fontweight', 'normal');
                const titleName = safeGet(props, 'widgetprop.props.extended.title.titlename', '');
                const titleFontSize = safeGet(props, 'widgetprop.props.extended.title.fontsize', '14px');
                const titleFontFamily = safeGet(props, 'widgetprop.props.extended.title.fontfamily', 'Arial');
                const titleFontWeight = safeGet(props, 'widgetprop.props.extended.title.fontweight', 'normal');
                const dataLabelsEnabled = safeGet(props, 'widgetprop.props.extended.dataLabels_enabled', false);
                const widgetWidth = safeGet(props, 'widgetprop.props.basic.width', 12);
                
                const legendFontSize = safeGet(props, 'widgetprop.props.extended.legend.fontsize', '12px');
                const legendFontFamily = safeGet(props, 'widgetprop.props.extended.legend.fontfamily', 'Arial');
                const legendFontWeight = safeGet(props, 'widgetprop.props.extended.legend.fontweight', 'normal');
                const legendMarkerRadius = safeGet(props, 'widgetprop.props.extended.legend.marker.radius', 2);
                const legendMarkerHeight = safeGet(props, 'widgetprop.props.extended.legend.marker.height', 8);
                const legendMarkerWidth = safeGet(props, 'widgetprop.props.extended.legend.marker.width', 8);

                const yaxisProperties = {
                    forceNiceScale: true,
                    labels: {
                        show: true,
                        style: {
                            colors: 'var(--axis-color)',
                            fontSize: fontSize,
                            fontFamily: fontFamily,
                            fontWeight: fontWeight,
                        },
                        formatter: (value) => { 
                            try {
                                if (typeof value === 'number' && !isNaN(value)) {
                                    return value.toFixed(2);
                                } else if (value == null) {
                                    return null;
                                } else {
                                    return value;
                                }
                            } catch {
                                return value;
                            }
                        },
                    },
                    title: {
                        text: titleName,
                        style: {
                            color: 'var(--axis-title-color)',
                            fontSize: titleFontSize,
                            fontFamily: titleFontFamily,
                            fontWeight: titleFontWeight,
                        },
                    },
                };

                const optionsConsumption = {
                    chart: {
                        zoom: {
                            enabled: false,
                        },
                        type: "line",
                        toolbar: {
                            show: false
                        },
                        offsetX: 0
                    },
                    stroke: { 
                        curve: 'straight',
                        show: true,
                        width: [...widthPropertiesLines],
                    },
                    grid: {
                        show: true,
                        borderColor: 'var(--grey-variation)',
                    },
                    fill: {
                        opacity: 1,
                        type: 'solid',
                    },
                    colors: [...colorPropertiesLines],
                    plotOptions: {
                        line: {
                            
                        }
                    },
                    dataLabels: {
                      enabled: dataLabelsEnabled
                    },
                    xaxis: {
                        type:'datetime',
                        tickAmount: (seriesConsumed[0] && seriesConsumed[0].data && seriesConsumed[0].data.length) ? 
                            Math.max(0, seriesConsumed[0].data.length - 1) : 0,
                        tickPlacement:'between',
                        tooltip: {
                            enabled: false
                        },
                        axisBorder: {
                            show: true,
                            color: 'var(--axis-color)',
                        },
                        axisTicks: {
                            show: true,
                            height: 3,
                            color: 'var(--axis-color)',
                        },
                        labels: {
                            show: true,
                            rotate: (Number(widgetWidth) < 6) ? -45 : 0,
                            style: {
                                colors: 'var(--axis-color)',
                                fontSize: fontSize,
                                fontFamily: fontFamily,
                                fontWeight: fontWeight,
                            },
                            formatter: function(value, timestamp, opts) {
                                try {
                                    if (isNaN(value)) {
                                        return '';
                                    }
                                    const index = safeGet(opts, 'i');
                                    const thistimestamp = (index !== undefined && opts && opts.w && opts.w.config && opts.w.config.series && opts.w.config.series[0] && opts.w.config.series[0].data) ? 
                                        safeGet(opts.w.config.series[0].data[index], 'x', '') : '';
                                    
                                    if(thistimestamp !== undefined && thistimestamp !== null && thistimestamp !== ''){
                                        const currentformatteddate = new Date(thistimestamp);
                                        if (isNaN(currentformatteddate.getTime())) {
                                            return '';
                                        }
                                        
                                        let currxvalue = '';
                                        const options = { timeZone: "Asia/Kolkata" };
                                        
                                        switch (dateformatselected) {
                                            case 'This Year':
                                            case 'Last Year':
                                                currxvalue = (currentformatteddate && typeof currentformatteddate.toLocaleDateString === 'function')
                                                    ? currentformatteddate.toLocaleDateString('en-us', { ...options, month: "short" }) : '';
                                                break;
                                            case 'This Month':
                                            case 'Last Month':
                                            case 'Last 30 days':
                                                currxvalue = (currentformatteddate && typeof currentformatteddate.toLocaleDateString === 'function')
                                                    ? currentformatteddate.toLocaleDateString('en-us', { ...options, day: "numeric" }) : '';
                                                break;
                                            case 'This Week':
                                            case 'Last Week':
                                            case 'Last 7 days':
                                                currxvalue = (currentformatteddate && typeof currentformatteddate.toLocaleDateString === 'function')
                                                    ? currentformatteddate.toLocaleDateString('en-us', { ...options, weekday: "short" }) : '';
                                                break;
                                            case 'Today':
                                            case 'Yesterday':
                                            case 'Last 24 hours':
                                                const hrs = currentformatteddate.getHours();
                                                let hrsstring = (typeof hrs === 'number' && hrs != null) ? hrs.toString() : '';
                                                if(hrsstring.length < 2 && hrsstring !== ''){
                                                    hrsstring = "0" + hrsstring;
                                                }
                                                const mins = currentformatteddate.getMinutes();
                                                let minsstring = (typeof mins === 'number' && mins != null) ? mins.toString() : '';
                                                if(minsstring.length < 2 && minsstring !== ''){
                                                    minsstring = "0" + minsstring;
                                                }
                                                currxvalue = (hrsstring !== '' && minsstring !== '') ? hrsstring + ":" + minsstring : '';
                                                if ((dateformatselected === 'Today' && Number(widgetWidth) <= 6) || 
                                                    (dateformatselected === 'Yesterday' && Number(widgetWidth) <= 6)) {
                                                    if( index % 2 === 0){
                                                        currxvalue = ''
                                                    }
                                                }
                                                break;
                                            case 'Custom':
                                                if (seriesConsumed[0] && seriesConsumed[0].data && seriesConsumed[0].data.length > 30) {
                                                    currxvalue = (currentformatteddate.getDate() === 1 && currentformatteddate && typeof currentformatteddate.toLocaleDateString === 'function')
                                                        ? currentformatteddate.toLocaleDateString('en-us', { ...options, day: "numeric", month: "short" }) 
                                                        : '';
                                                } else {
                                                    currxvalue = (currentformatteddate && typeof currentformatteddate.toLocaleDateString === 'function')
                                                        ? currentformatteddate.toLocaleDateString('en-us', { ...options, day: "numeric" }) : '';
                                                }
                                                break;
                                            default:
                                                currxvalue = '';
                                                break;
                                        }
                                        return currxvalue || '';
                                    }
                                    return '';
                                } catch (err) {
                                    return '';
                                }
                            }
                        },
                    },
                    yaxis: yaxisProperties,
                    tooltip: {
                        enabled: true,
                        enabledOnSeries: undefined,
                        custom: function({ series, seriesIndex, dataPointIndex, w }) {
                            try {
                                const value = series[seriesIndex][dataPointIndex];
                                const timestamp = w.globals.seriesX[seriesIndex][dataPointIndex];
                                const currentFormattedDate = new Date(timestamp);
                                
                                if (isNaN(currentFormattedDate.getTime())) {
                                    return '<div style="padding:5px; background-color:#005C8E;">Invalid date</div>';
                                }
                                
                                let tooltipString = `<div style="display:flex; flex-direction:column; padding:5px; background-color:#005C8E;">`;
                                const options = { 
                                    year: 'numeric', 
                                    month: '2-digit', 
                                    day: '2-digit', 
                                    hour: '2-digit', 
                                    minute: '2-digit', 
                                    hour12: false 
                                }; 
                                const formattedDate = (currentFormattedDate && typeof currentFormattedDate.toLocaleDateString === 'function')
                                    ? currentFormattedDate.toLocaleDateString('en-US', options).replace(/(\d{1,2})\/(\d{1,2})\/(\d{4})/g, '$3-$1-$2')
                                    : '';
                                tooltipString += `<div style="font-weight:600; color:#FFFFFF; background-color:#005C8E;">${formattedDate}</div>`;
                                
                                for (let i = 0; i < series.length; i++) {
                                    const seriesValue = series[i][dataPointIndex];
                                    const seriesName = (seriesConsumed[i] && seriesConsumed[i].name) ? seriesConsumed[i].name : '';
                                    let valueString = '';
                                    if (seriesValue == null) {
                                        valueString = 'No Value';
                                    } else if (typeof seriesValue === 'number' && !isNaN(seriesValue)) {
                                        valueString = seriesValue.toFixed(2);
                                    } else {
                                        valueString = seriesValue;
                                    }
                                    const color = (w.config && w.config.colors && w.config.colors[i]) ? w.config.colors[i] : '#000000';
                                    tooltipString += `
                                    <div style="display:flex; align-items:center; margin-bottom:4px; background-color:#005C8E;">
                                        <div style="height:8px; width:8px; background-color:${color}; border-radius:50%; margin-right:8px;"></div>
                                        <div style="font-weight:600; color:#FFFFFF;">${seriesName}</div>
                                        <div style="margin-left:12px; color:#FFFFFF;">${valueString}</div>
                                    </div>
                                    `;
                                }
                                tooltipString += `</div>`;
                                return tooltipString;
                            } catch (err) {
                                return '<div style="padding:5px; background-color:#005C8E;">Error displaying tooltip</div>';
                            }
                        },
                        hideEmptySeries: true,
                        fillSeriesColor: false,
                        theme: false,
                        borderRadius: 0,
                        style: {
                          fontSize: "12px",
                          fontWeight: 800,
                          fontFamily: 'Roboto',
                        },
                    },
                    legend: {
                        show: true,
                        fontSize: legendFontSize,
                        fontFamily: legendFontFamily,
                        fontWeight: legendFontWeight,
                        labels: {
                            colors: Array(colorPropertiesLines.length).fill('var(--legend-text-color)')
                        },
                        customLegendItems: [...legendNamePropertiesLines],
                        markers: {
                            radius: legendMarkerRadius,
                            height: legendMarkerHeight,
                            width: legendMarkerWidth,
                            fillColors: [...colorPropertiesLines]
                        }
                    },
                };

                
                setDebugInfo('Setting chart data and options...');
                setSeriesChart(seriesConsumed);
                setOptions(optionsConsumption);
                setHasData(true);
                setIsLoading(false);
                
                
            } catch (err) {
               
                setError(err.message || 'Unknown error occurred');
                setIsLoading(false);
                setHasData(false);
                setDebugInfo(`Error: ${err.message}`);
            }
        };

        // Add a small delay to ensure proper loading state display
        const timer = setTimeout(() => {
            loadChart();
        }, 50);

        return () => clearTimeout(timer);
    }, [props.widgetprop, tagcollectionvaluesnewjson, tagaliascollectionvaluesnewjson, dateformatselected]);

    // Show loading spinner
    if (isLoading) {
        return <LoadingSpinner />;
    }

    // Show error state with debug info
    if (error) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '85%',
                width: '100%',
                padding: '20px'
            }}>
                <div style={{
                    color: '#ff6b6b',
                    fontSize: '16px',
                    textAlign: 'center',
                    marginBottom: '10px'
                }}>
                    ...loading chart
                </div>
                <div style={{
                    color: '#ff6b6b',
                    fontSize: '12px',
                    textAlign: 'center',
                    marginBottom: '10px'
                }}>
                    {error}
                </div>
                {debugInfo && (
                    <div style={{
                        color: '#999',
                        fontSize: '10px',
                        textAlign: 'center',
                        fontFamily: 'monospace',
                        backgroundColor: '#f5f5f5',
                        padding: '5px',
                        borderRadius: '3px',
                        maxWidth: '300px',
                        wordBreak: 'break-word'
                    }}>
                        {}
                    </div>
                )}
            </div>
        );
    }

    // Show empty state if no data - with more specific checks
    if (!hasData || !seriesChart || seriesChart.length === 0 || 
        !seriesChart.some(series => series && series.data && series.data.length > 0)) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '85%',
                width: '100%',
                color: '#666',
                fontSize: '14px',
                padding: '20px'
            }}>
                <div>📊 No data available</div>
                {debugInfo && (
                    <div style={{
                        color: '#999',
                        fontSize: '10px',
                        textAlign: 'center',
                        fontFamily: 'monospace',
                        marginTop: '10px',
                        backgroundColor: '#f5f5f5',
                        padding: '5px',
                        borderRadius: '3px',
                        maxWidth: '300px',
                        wordBreak: 'break-word'
                    }}>
                        Debug: {debugInfo}
                    </div>
                )}
            </div>
        );
    }

    return (
        <>
            <Chart 
                options={options} 
                series={seriesChart} 
                type='line' 
                height="85%" 
                width="100%" 
            />
        </>
    );
};

export default LineChartnewjson;